import type { Prim } from './custom';
import type { Colour, Item } from './drawing';
import type { DxfFile } from './dxfImport';
import { t as $t } from './i18n';


/*
 * Front views of parts for the panel layout: either vector drawings (from the
 * manufacturer's 2D DXF, stored at 1:1 in mm) or raster images (PNG/JPEG).
 * Vector data is stored as validated primitives, never as markup.
 */

export interface FrontVector {
  kind: 'vec';
  /** real size in mm */
  w: number;
  h: number;
  /** primitives in mm, origin top-left, y down */
  prims: Prim[];
}
export interface FrontImage {
  kind: 'img';
  /** pixel size */
  w: number;
  h: number;
  mime: 'image/png' | 'image/jpeg';
}
export type Front = FrontVector | FrontImage;
export type FrontLookup = (pn: string) => Front | undefined;

export class FrontFormatError extends Error {}

const UNIT_MM: Record<string, number> = { mm: 1, cm: 10, m: 1000, 'inç': 25.4, feet: 304.8 };
const r2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Converts a front-view DXF (usually one device drawn 1:1) into a vector front view.
 * All model-space drawings are merged; files with only blocks use the largest block.
 */
export function frontFromDxf(f: DxfFile): FrontVector {
  const model = f.candidates.filter((c) => c.source === 'model');
  const cands = model.length
    ? model
    : [...f.candidates].sort((a, b) => (b.bb[2] - b.bb[0]) * (b.bb[3] - b.bb[1]) - (a.bb[2] - a.bb[0]) * (a.bb[3] - a.bb[1])).slice(0, 1);
  if (!cands.length) throw new FrontFormatError($t("DXF dosyasında çizim yok."));
  const geo = cands.flatMap((c) => c.geo);
  const x0 = Math.min(...cands.map((c) => c.bb[0])), y1 = Math.max(...cands.map((c) => c.bb[3]));
  const x1 = Math.max(...cands.map((c) => c.bb[2])), y0 = Math.min(...cands.map((c) => c.bb[1]));
  const k = UNIT_MM[f.units] ?? 1;
  const T = (p: [number, number]): [number, number] => [r2((p[0] - x0) * k), r2((y1 - p[1]) * k)];
  const prims: Prim[] = [];
  for (const g of geo) {
    if (g.k === 'line') {
      let pts = g.pts.map(T);
      if (g.closed) pts = [...pts, pts[0]];
      pts = pts.filter((p, i) => i === 0 || p[0] !== pts[i - 1][0] || p[1] !== pts[i - 1][1]);
      if (pts.length >= 2) prims.push({ k: 'line', pts: pts.slice(0, 400), ...(g.dash ? { dash: true } : {}) });
    } else if (g.k === 'circle') {
      const [x, y] = T(g.c);
      prims.push({ k: 'circle', x, y, r: r2(g.r * k) });
    } else {
      const size = r2(Math.min(50, Math.max(0.5, (g.h * k) / 0.72)));
      const [x, y] = T(g.p);
      prims.push({ k: 'text', x: r2(x + (g.text.length * size * 0.56) / 2), y, size, text: g.text.slice(0, 40) });
    }
  }
  return validateFrontVector({ kind: 'vec', w: r2((x1 - x0) * k), h: r2((y1 - y0) * k), prims });
}

const LIM = 5000;
const n = (v: unknown) => {
  const x = Number(v);
  if (!Number.isFinite(x) || Math.abs(x) > LIM) throw new FrontFormatError($t("Ön görünüşte geçersiz koordinat."));
  return r2(x);
};
const txt = (v: unknown) => String(v ?? '').replace(/[<>&"]/g, '').slice(0, 40);

/** Validates vector front-view JSON from clients (server side) or files. */
export function validateFrontVector(input: unknown): FrontVector {
  const d = (input ?? {}) as Record<string, any>;
  const w = n(d.w), h = n(d.h);
  if (w <= 0 || h <= 0) throw new FrontFormatError($t("Ön görünüşün genişliği ve yüksekliği sıfırdan büyük olmalı."));
  const raw = Array.isArray(d.prims) ? d.prims : [];
  if (raw.length > 8000) throw new FrontFormatError($t("Ön görünüş çok karmaşık (en fazla 8000 çizim öğesi)."));
  const prims: Prim[] = raw.map((p: any): Prim => {
    switch (p?.k) {
      case 'line':
        return { k: 'line', pts: (Array.isArray(p.pts) ? p.pts : []).slice(0, 400).map((q: any) => [n(q?.[0]), n(q?.[1])] as [number, number]), ...(p.dash ? { dash: true } : {}) };
      case 'rect':
        return { k: 'rect', x: n(p.x), y: n(p.y), w: Math.abs(n(p.w)), h: Math.abs(n(p.h)), ...(p.fill ? { fill: true } : {}) };
      case 'circle':
        return { k: 'circle', x: n(p.x), y: n(p.y), r: Math.abs(n(p.r)), ...(p.fill ? { fill: true } : {}) };
      case 'text':
        return { k: 'text', x: n(p.x), y: n(p.y), size: Math.min(50, Math.max(0.5, n(p.size ?? 2.5))), text: txt(p.text) };
      default:
        throw new FrontFormatError($t("Ön görünüşte bilinmeyen çizim öğesi."));
    }
  }).filter((p: Prim) => p.k !== 'line' || p.pts.length >= 2);
  if (!prims.length) throw new FrontFormatError($t("Ön görünüş boş."));
  return { kind: 'vec', w, h, prims };
}

/**
 * Draws a front view into a sheet rectangle (x, y, w, h in sheet mm), keeping its
 * aspect ratio and centring it. Raster images become an image item.
 */
export function frontItems(f: Front, pn: string, x: number, y: number, w: number, h: number, colour: Colour = 'ink'): Item[] {
  const s = Math.min(w / f.w, h / f.h), ox = x + (w - f.w * s) / 2, oy = y + (h - f.h * s) / 2;
  if (f.kind === 'img') return [{ k: 'image', x: ox, y: oy, w: f.w * s, h: f.h * s, src: pn, layer: 'symbol' }];
  const P = (px: number, py: number): [number, number] => [ox + px * s, oy + py * s];
  const width = Math.max(0.08, Math.min(0.25, 0.25 * s * 4));
  const out: Item[] = [];
  for (const p of f.prims) {
    if (p.k === 'line') out.push({ k: 'poly', pts: p.pts.map((q) => P(q[0], q[1])), stroke: colour, width, dash: p.dash ? [1, 0.8] : undefined, layer: 'symbol' });
    else if (p.k === 'rect') {
      const a = P(p.x, p.y), b = P(p.x + p.w, p.y + p.h);
      out.push({ k: 'poly', pts: [a, [b[0], a[1]], b, [a[0], b[1]]], closed: true, stroke: colour, fill: p.fill ? colour : undefined, width, layer: 'symbol' });
    } else if (p.k === 'circle') out.push({ k: 'circle', c: P(p.x, p.y), r: p.r * s, stroke: colour, fill: p.fill ? colour : undefined, width, layer: 'symbol' });
    else if (p.size * s >= 0.6) {
      const [tx, ty] = P(p.x, p.y);
      out.push({ k: 'text', x: tx, y: ty, size: p.size * s, text: p.text, anchor: 'middle', font: 'ui', colour: 'ink2', layer: 'symbol' });
    }
  }
  return out;
}
